import React from 'react';
import { useDropzone } from 'react-dropzone';
import { Typography, Paper, Box, Divider } from '@mui/material';
import { PiCloudArrowUp } from 'react-icons/pi';
import { IManageFileSelector } from './iManage/IManageFileSelector';

interface FileUploadZoneProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  acceptedFileTypes?: string[];
  showIManageOption?: boolean;
  practice?: string;
  jobNumber?: string;
}

export default function FileUploadZone({ onFilesSelected, disabled = false, acceptedFileTypes, showIManageOption, practice, jobNumber }: FileUploadZoneProps) {
  const imanageEnabled = process.env.REACT_APP_IMANAGE_ENABLED === 'true';
  const shouldShowIManage = showIManageOption !== false && imanageEnabled;
  // Map file formats to MIME types and extensions
  const getAcceptConfig = (formats?: string[]) => {
    // Accept all file types when no specific formats provided
    if (!formats) {
      return undefined; // No restrictions
    }
    
    const mimeTypeMap: Record<string, { mime: string; ext: string }> = {
      // Documents
      'pdf': { mime: 'application/pdf', ext: '.pdf' },
      'docx': { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', ext: '.docx' },
      'xlsx': { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ext: '.xlsx' },
      'pptx': { mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', ext: '.pptx' },
      'json': { mime: 'application/json', ext: '.json' },
      'txt': { mime: 'text/plain', ext: '.txt' },
      'csv': { mime: 'text/csv', ext: '.csv' },
      // Video formats
      'mp4': { mime: 'video/mp4', ext: '.mp4' },
      'mov': { mime: 'video/quicktime', ext: '.mov' },
      'avi': { mime: 'video/x-msvideo', ext: '.avi' },
      'webm': { mime: 'video/webm', ext: '.webm' },
      'wmv': { mime: 'video/x-ms-wmv', ext: '.wmv' },
      // Audio formats
      'mp3': { mime: 'audio/mpeg', ext: '.mp3' },
      'wav': { mime: 'audio/wav', ext: '.wav' },
      'm4a': { mime: 'audio/x-m4a', ext: '.m4a' },
      // Images
      'jpg': { mime: 'image/jpeg', ext: '.jpg' },
      'jpeg': { mime: 'image/jpeg', ext: '.jpeg' },
      'png': { mime: 'image/png', ext: '.png' },
      'gif': { mime: 'image/gif', ext: '.gif' },
      'webp': { mime: 'image/webp', ext: '.webp' },
    };
    
    const acceptConfig: Record<string, string[]> = {};
    formats.forEach(format => {
      const config = mimeTypeMap[format.toLowerCase()];
      if (config) {
        acceptConfig[config.mime] = [config.ext];
      }
    });
    
    return acceptConfig;
  };
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: getAcceptConfig(acceptedFileTypes),
    multiple: true,
    disabled,
    onDrop: onFilesSelected
  });

  const handleIManageFilesSelect = (files: File[]) => {
    onFilesSelected(files);
  };

  return (
    <Box>
      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          opacity: disabled ? 0.5 : 1,
          '&:hover': {
            borderColor: disabled ? 'grey.300' : 'primary.main',
            backgroundColor: disabled ? 'background.paper' : 'action.hover'
          }
        }}
      >
        <input {...getInputProps()} />
        <PiCloudArrowUp size={48} style={{ color: '#bdbdbd', marginBottom: 16 }} />
        <Typography variant="h6" gutterBottom>
          {isDragActive ? 'Drop files here' : 'Upload Documents'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Drag & drop files here, or click to select
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          Supported: {acceptedFileTypes ? 
            acceptedFileTypes.map(type => type.toUpperCase()).join(', ') : 
            'All file types'
          }
        </Typography>
      </Paper>
      
      {shouldShowIManage && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">OR</Typography>
          </Divider>
          <IManageFileSelector
            onFilesSelected={handleIManageFilesSelect}
            disabled={disabled}
            variant="outlined"
            fullWidth
            size="large"
            practice={practice}
            jobNumber={jobNumber}
          />
        </>
      )}
    </Box>
  );
}